import React, { useMemo, useState } from 'react';
import { Copy, Library, UserRound, UsersRound } from 'lucide-react';
import { PathwaySource, PathwayTemplate, SavedPathway, LEVEL_LABELS } from '../../types/pathway';
import { moduleById } from '../../data/skillModules';
import { PathwayButton } from './PathwayButton';
interface CatalogItem {
  id: string;
  name: string;
  audience: SavedPathway['audience'];
  summary: string;
  moduleIds: string[];
  source: PathwaySource;
  kind: 'saved' | 'template';
  item: SavedPathway | PathwayTemplate;
}
interface PathwayCatalogProps {
  savedPathways: SavedPathway[];
  templates: PathwayTemplate[];
  onUseSavedPathway: (pathway: SavedPathway) => void;
  onUseTemplate: (template: PathwayTemplate) => void;
}
const SOURCE_OPTIONS: {
  value: PathwaySource | 'all';
  label: string;
  icon: typeof UserRound;
}[] = [{
  value: 'all',
  label: 'All sources',
  icon: Library
}, {
  value: 'mine',
  label: 'My pathways',
  icon: UserRound
}, {
  value: 'shared',
  label: 'Shared by others',
  icon: UsersRound
}, {
  value: 'university',
  label: 'University of Phoenix',
  icon: Library
}];
const SOURCE_LABELS: Record<PathwaySource, string> = {
  mine: 'My pathway',
  shared: 'Shared by others',
  university: 'University of Phoenix'
};
export function PathwayCatalog({
  savedPathways,
  templates,
  onUseSavedPathway,
  onUseTemplate
}: PathwayCatalogProps) {
  const [source, setSource] = useState<PathwaySource | 'all'>('all');
  const [query, setQuery] = useState('');
  const catalog = useMemo<CatalogItem[]>(() => [...savedPathways.map(pathway => ({
    id: pathway.id,
    name: pathway.name,
    audience: pathway.audience,
    summary: `Saved for ${pathway.organization}`,
    moduleIds: pathway.moduleIds,
    source: pathway.source,
    kind: 'saved' as const,
    item: pathway
  })), ...templates.map(template => ({
    id: template.id,
    name: template.name,
    audience: template.audience,
    summary: template.summary,
    moduleIds: template.moduleIds,
    source: template.source,
    kind: 'template' as const,
    item: template
  }))], [savedPathways, templates]);
  const visible = catalog.filter(item => {
    const searchable = `${item.name} ${item.summary} ${item.moduleIds.map(id => moduleById(id)?.name ?? '').join(' ')}`.toLowerCase();
    return (source === 'all' || item.source === source) && searchable.includes(query.trim().toLowerCase());
  });
  return <section aria-labelledby="pathway-catalog-heading" data-id="element-1698" data-mp="b5lo9trn51">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3" data-id="element-1699" data-mp="3s67xnn1boonk">
        <div data-id="element-1700" data-mp="a01zpouh404l">
          <h2 id="pathway-catalog-heading" className="text-xl font-bold text-gray-900" data-id="element-1701" data-mp="9ztl9sipcmra">Start from a pathway</h2>
          <p className="mt-1 text-sm text-gray-600" data-id="element-1702" data-mp="50gxn5k0bbnn">Use a proven sequence, a colleague’s example, or one of your saved pathways as a starting point.</p>
        </div>
        <span className="text-sm text-gray-500" data-id="element-1703" data-mp="2bt9odovz7u35">{visible.length} available</span>
      </div>
      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm" data-id="element-1704" data-mp="26ug7dmhhdky8">
        <div className="flex flex-wrap gap-2" aria-label="Filter pathways by source" data-id="element-1705" data-mp="mb31zz9lygmy">
          {SOURCE_OPTIONS.map(option => {
          const Icon = option.icon;
          const active = source === option.value;
          return <button key={option.value} type="button" onClick={() => setSource(option.value)} aria-pressed={active} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${active ? 'border-[#30718D] bg-[#30718D]/5 text-[#265a70]' : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'}`} data-id="element-1706" data-mp="hjl11b7l4g1z"><Icon size={14} data-id="element-1707" data-mp="3psbgqa91h0dg" />{option.label}</button>;
        })}
        </div>
        <label className="mt-3 block" data-id="element-1708" data-mp="3upbcnsc6cmqu">
          <span className="sr-only" data-id="element-1709" data-mp="3uqd1czm457r1">Search pathways</span>
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search pathways by name or skill" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1710" data-mp="1nzewpxx5inh7" />
        </label>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2" data-id="element-1711" data-mp="3l12o989ff22i">
        {visible.map(item => <article key={`${item.kind}-${item.id}`} className="flex min-h-[220px] flex-col rounded-xl border border-gray-100 bg-white p-5 shadow-sm" data-id="element-1712" data-mp="3fzd8jfb9e083">
            <div className="flex items-start justify-between gap-3" data-id="element-1713" data-mp="363jzw70nuqvl">
              <div data-id="element-1714" data-mp="364v3o6u3vymp"><p className="text-xs font-semibold uppercase tracking-wider text-[#30718D]" data-id="element-1715" data-mp="364uzdun2pcgw">{SOURCE_LABELS[item.source]}</p><h3 className="mt-1 text-base font-bold text-gray-900" data-id="element-1716" data-mp="2xaq6l5odpqyq">{item.name}</h3></div>
              <span className="whitespace-nowrap text-xs text-gray-500" data-id="element-1717" data-mp="2x8cf16swqwvx">{item.moduleIds.length} modules</span>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-gray-600" data-id="element-1718" data-mp="2nd27y4nnjzba">{item.summary}</p>
            <p className="mt-3 text-xs font-medium text-gray-500" data-id="element-1719" data-mp="2idpprx7q7gf1">Built for {LEVEL_LABELS[item.audience]}</p>
            <ol className="mt-3 space-y-1 border-l-2 border-[#30718D]/15 pl-3 text-xs text-gray-600" data-id="element-1720" data-mp="28a0tthehe8lo">
              {item.moduleIds.slice(0, 3).map((moduleId, index) => <li key={moduleId} className="truncate" data-id="element-1721" data-mp="2daouyvwfc5tm" data-mpi={index}><span className="mr-2 font-bold text-[#30718D]" data-id="element-1722" data-mp="2i75j3q3mik6p">{index + 1}</span>{moduleById(moduleId)?.name ?? 'Skill module'}</li>)}
              {item.moduleIds.length > 3 && <li className="text-gray-500" data-id="element-1723" data-mp="2n4p4rb2nhz8o">+ {item.moduleIds.length - 3} more modules</li>}
            </ol>
            <div className="mt-auto pt-4" data-id="element-1724" data-mp="1ph6olf6ntuun"><PathwayButton size="sm" variant="secondary" onClick={() => item.kind === 'saved' ? onUseSavedPathway(item.item as SavedPathway) : onUseTemplate(item.item as PathwayTemplate)} data-id="element-1725" data-mp="1uhkyl2rtnevb"><Copy size={15} data-id="element-1726" data-mp="247xeuislslvn" />Use as a starting point</PathwayButton></div>
          </article>)}
      </div>
      {visible.length === 0 && <div className="mt-4 rounded-xl border border-gray-100 bg-white px-6 py-10 text-center text-sm text-gray-600 shadow-sm" data-id="element-1727" data-mp="16rgvqxmgjj5d">No pathways match this source or search.</div>}
    </section>;
}